import { useMemo, useState, useEffect } from 'react'
import { FrontConversation } from '@/types/front'
import { detectLoadNumber, LoadDetectionResult } from '@/utils/loadDetection'
import { frontApi } from '@/services/api'
import { logger } from '@/utils/logger'

export type MatchType = 'linked' | 'tag' | 'subject' | 'none'

export interface UseLoadNumberReturn extends Omit<
  LoadDetectionResult,
  'matchType'
> {
  matchType: MatchType
  loading: boolean
  linkedLoadId?: string // If found via linked conversation
}

export const useLoadNumber = (
  conversation: FrontConversation | null
): UseLoadNumberReturn => {
  const [linkedLoad, setLinkedLoad] = useState<{
    conversationId: string
    loadId: string
    loadNumber?: string
  } | null>(null)
  const [linkLoading, setLinkLoading] = useState(false)
  const conversationId = conversation?.id

  // Check for linked conversation (via B18 app)
  useEffect(() => {
    // Set by cleanup, so a check for a previous conversation never lands
    let cancelled = false

    const checkLinkedLoad = async () => {
      setLinkedLoad(null)
      if (!conversationId) {
        setLinkLoading(false)
        return
      }

      setLinkLoading(true)
      try {
        const result = await frontApi.getLinkedLoad(conversationId)
        if (!cancelled) {
          setLinkedLoad(result ? { ...result, conversationId } : null)
        }
      } catch (error) {
        logger.warn('Failed to check linked load', { error })
      } finally {
        if (!cancelled) setLinkLoading(false)
      }
    }

    checkLinkedLoad()
    return () => {
      cancelled = true
    }
  }, [conversationId])

  // Detect load number from tags/subject
  const detectionResult = useMemo((): LoadDetectionResult => {
    if (!conversation) {
      return {
        loadNumber: null,
        matchType: 'none',
        confidence: 'low'
      }
    }

    // Extract tag names from conversation
    const tagNames = conversation.tags?.map(tag => tag.name) || []

    // Get subject
    const subject = conversation.subject || ''

    // Detect load number
    return detectLoadNumber(tagNames, subject)
  }, [conversation])

  // Ignore a link stored for another conversation on the render before the effect clears it
  const currentLink =
    linkedLoad?.conversationId === conversationId ? linkedLoad : null

  // Priority: linked > tag > subject
  // If linked via B18, use that first
  if (currentLink?.loadNumber || currentLink?.loadId) {
    return {
      loadNumber: currentLink.loadNumber || null,
      matchType: 'linked',
      confidence: 'high',
      loading: linkLoading,
      linkedLoadId: currentLink.loadId
    }
  }

  return {
    ...detectionResult,
    loading: linkLoading
  }
}

export default useLoadNumber
